import _ from 'lodash'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'
import useFarwestPmixSubcategoryData from './useFarwestPmixSubcategoryData'
import useSequentialTrendUnifiedData from './useSequentialTrendUnifiedData'
import useUnifiedData from './useUnifiedData'

export const farwestPmixItemDataConfigs = {
  categoryName: 'string',
  subcategoryName: 'string',
  itemName: 'string',
  itemCode: 'string',
  totalSales: 'price',
  quantitySold: 'number',
  salesPercentage: 'percent',

  currentWeekEndDate: 'date-string',
  salesPercentageWeek1InPast: 'percent',
  salesPercentageWeek2InPast: 'percent',
  salesPercentageWeek3InPast: 'percent',
  salesPercentageWeek4InPast: 'percent',

  quantitySoldWeek1InPast: 'number',
  quantitySoldWeek2InPast: 'number',
  quantitySoldWeek3InPast: 'number',
  quantitySoldWeek4InPast: 'number',

  totalSalesWeek1InPast: 'price',
  totalSalesWeek2InPast: 'price',
  totalSalesWeek3InPast: 'price',
  totalSalesWeek4InPast: 'price',
} as const

const useFarwestPmixItemData = () => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { data: subcategoryPmixData, loading: subcategoryLoading } =
    useFarwestPmixSubcategoryData()
  const {
    variables: { isTrend, fwWingstopProductMixItems },
  } = useVariables()

  const subcategoryCodeToDetailsMap: {
    [subcategoryCode: string]: {
      subcategoryName: string
      subcategoryCode: string
      categoryName: string
      categoryCode: string
    }
  } = {}

  if (subcategoryPmixData && subcategoryPmixData.source) {
    subcategoryPmixData.source.forEach((rowData) => {
      if (rowData?.subcategoryCode) {
        subcategoryCodeToDetailsMap[rowData.subcategoryCode as string] = {
          subcategoryName: rowData.subcategoryName as string,
          subcategoryCode: rowData.subcategoryCode as string,
          categoryName: rowData.categoryName as string,
          categoryCode: rowData.categoryCode as string,
        }
      }
    })
  }

  const {
    data: trendData,
    uniqueKey,
    loading: trendLoading,
  } = useSequentialTrendUnifiedData({
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iQueryType: 'ITEM',
      iFilter: {
        location_group_ids: groupFilter?.ids,
        intersected_location_group_ids: groupFilter?.intersectedIds,
        item_category_codes: ['EXPO_MENU_CATEGORY_ROOT'],
        metrics: [
          'total_sales',
          'quantity_sold',
          'sales_percentage',
          'location_total_sales',
        ],
      },
    },
    skip: !isTrend || !endDate || !groupFilter,
  })

  const { data: itemData, loading: itemDataLoading } = useUnifiedData({
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iQueryType: 'ITEM',
      iFilter: {
        location_group_ids: groupFilter?.ids,
        intersected_location_group_ids: groupFilter?.intersectedIds,
        item_category_codes: ['EXPO_MENU_CATEGORY_ROOT'],
        metrics: [
          'total_sales',
          'quantity_sold',
          'sales_percentage',
          'location_total_sales',
        ],
      },
    },
    skip:
      isTrend ||
      !subcategoryPmixData ||
      !startDate ||
      !endDate ||
      !groupFilter ||
      !hasGroupBy,
  })

  return {
    data: useMemo((): IApiDataType => {
      const originalData = isTrend ? trendData : itemData

      const source = originalData?.source?.reduce((s: any, r: any) => {
        const subcategoryDetails =
          subcategoryCodeToDetailsMap[r?.displayParentCategoryCode]

        return !subcategoryDetails
          ? s
          : [
              ...s,
              {
                ...r,
                itemName: r.itemName,
                itemCode: r.itemCode,
                subcategoryName: subcategoryDetails.subcategoryName,
                subcategoryCode: subcategoryDetails.subcategoryCode,
                categoryName: subcategoryDetails.categoryName,
                categoryCode: subcategoryDetails.categoryCode,
              },
            ]
      }, [] as { [key: string]: unknown }[])

      if (!source) return null

      const isExistingItems =
        fwWingstopProductMixItems.fwWingstopProductMixItems &&
        fwWingstopProductMixItems.fwWingstopProductMixItems.length > 0

      const filteredSource = _.orderBy(source, [
        'categoryName',
        'subcategoryName',
        'itemName',
      ]).filter((s) => {
        if (!isExistingItems) return false
        return fwWingstopProductMixItems.fwWingstopProductMixItems.includes(
          s.itemCode,
        )
      })

      // When filter is active, recalculate summary by summing numeric fields from filtered source
      const summary = (() => {
        if (filteredSource.length === 0) return {}

        const originalSummary = originalData?.summary
        const recalculated: { [key: string]: number } = {}
        filteredSource.forEach((item) => {
          Object.entries(item).forEach(([key, value]) => {
            if (typeof value === 'number') {
              recalculated[key] = (recalculated[key] ?? 0) + value
            }
          })
        })

        return {
          ...originalSummary, // keep non-numeric fields (e.g. currentWeekEndDate, categoryName)
          ...recalculated, // override with summed numeric values from filtered source
        }
      })()

      return {
        source: filteredSource,
        summary,
      }
    }, [
      itemData,
      subcategoryPmixData,
      trendData,
      fwWingstopProductMixItems.fwWingstopProductMixItems,
    ]),
    uniqueKey: isTrend ? uniqueKey : undefined,
    loading: subcategoryLoading || itemDataLoading || trendLoading,
  }
}

export default useFarwestPmixItemData
